/* global gdAlertify */
import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";

/**
 * Umbrales de alerta para valores de turno, colación y movilización.
 *
 * Se configuran en Administración > Umbrales de Valores y NO bloquean el guardado:
 * solo advierten cuando el monto ingresado supera el máximo esperado.
 * Un umbral en 0 desactiva la alerta de ese concepto.
 */

export const CONCEPTOS_UMBRAL = {
    turno: "Valor turno",
    colacion: "Colación",
    movilizacion: "Movilización",
};

const UMBRALES_VACIOS = { turno: 0, colacion: 0, movilizacion: 0 };

let cacheUmbrales = null;
let peticionEnCurso = null;

/** Carga los umbrales una sola vez y los reutiliza (se comparten entre modales). */
export const obtenerUmbrales = async () => {
    if (cacheUmbrales) return cacheUmbrales;

    if (!peticionEnCurso) {
        peticionEnCurso = axiosInstance
            .get("/configuracion/umbrales/lista")
            .then((res) => {
                cacheUmbrales = { ...UMBRALES_VACIOS, ...(res.data?.data || {}) };
                return cacheUmbrales;
            })
            .catch((error) => {
                // Si la configuración no está disponible no se rompe el formulario:
                // simplemente no se alerta.
                console.error("No se pudieron cargar los umbrales de valores:", error);
                return UMBRALES_VACIOS;
            })
            .finally(() => {
                peticionEnCurso = null;
            });
    }

    return peticionEnCurso;
};

/** Fuerza recarga tras guardar la configuración. */
export const invalidarCacheUmbrales = () => {
    cacheUmbrales = null;
};

export const useUmbralesValores = () => {
    const [umbrales, setUmbrales] = useState(cacheUmbrales);

    useEffect(() => {
        let vigente = true;
        obtenerUmbrales().then((data) => {
            if (vigente) setUmbrales(data);
        });
        return () => {
            vigente = false;
        };
    }, []);

    return umbrales;
};

export const formatearMonto = (valor) => Number(valor || 0).toLocaleString("es-CL", {
    maximumFractionDigits: 0,
});

/**
 * Devuelve los conceptos cuyo valor supera el umbral configurado.
 * @param {{turno?:number, colacion?:number, movilizacion?:number}} valores
 * @param {object} umbrales
 */
export const detectarExcesosUmbral = (valores, umbrales) => {
    if (!umbrales || !valores) return [];

    return Object.entries(CONCEPTOS_UMBRAL).reduce((acc, [concepto, etiqueta]) => {
        const umbral = Number(umbrales[concepto] || 0);
        const valor = Number(valores[concepto] || 0);

        if (umbral > 0 && valor > umbral) {
            acc.push({ concepto, etiqueta, valor, umbral, exceso: valor - umbral });
        }
        return acc;
    }, []);
};

/**
 * Mensaje compacto de una linea para el confirm.
 * gdAlertify no interpreta saltos de linea ni HTML en `message`, asi que el
 * detalle extenso se muestra en <AlertaUmbrales /> dentro del formulario.
 */
export const construirMensajeExcesos = (excesos) => excesos
    .map((e) => `${e.etiqueta} $${formatearMonto(e.valor)} (habitual $${formatearMonto(e.umbral)})`)
    .join(" · ");

/**
 * Ejecuta `onConfirm` directamente si no hay excesos; si los hay, pide una
 * confirmación extra antes. Nunca impide guardar.
 */
export const confirmarSiExcedeUmbral = ({ valores, umbrales, onConfirm, onCancel }) => {
    const excesos = detectarExcesosUmbral(valores, umbrales);

    if (excesos.length === 0) {
        onConfirm();
        return;
    }

    const alertify = typeof window !== "undefined" ? window.gdAlertify : undefined;
    if (!alertify?.confirm) {
        onConfirm();
        return;
    }

    alertify.confirm({
        title: "⚠️ Valor más alto de lo habitual",
        message: `${construirMensajeExcesos(excesos)}. Es un valor más alto que el promedio. ¿Desea continuar de todas formas?`,
        onConfirm,
        onCancel: onCancel || (() => {}),
    });
};

/**
 * Versión await-able para flujos lineales (`if (!(await confirmar...)) return;`).
 * Resuelve true si no hay excesos o si el usuario acepta continuar.
 */
export const confirmarSiExcedeUmbralAsync = ({ valores, umbrales }) => new Promise((resolve) => {
    confirmarSiExcedeUmbral({
        valores,
        umbrales,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
    });
});

/** Aviso visual en línea, para que se vea mientras se escribe. */
export const AlertaUmbrales = ({ valores, umbrales, className = "" }) => {
    const excesos = detectarExcesosUmbral(valores, umbrales);
    if (excesos.length === 0) return null;

    return (
        <div className={`alert alert-warning py-2 px-3 mb-2 ${className}`}>
            <div className="fw-semibold mb-1">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Valor más alto de lo habitual
            </div>
            <ul className="mb-0 ps-3 small">
                {excesos.map((e) => (
                    <li key={e.concepto}>
                        <strong>{e.etiqueta}:</strong> ${formatearMonto(e.valor)}
                        {" "}es más alto que el promedio de ${formatearMonto(e.umbral)}
                    </li>
                ))}
            </ul>
            <div className="small text-muted mt-1">
                Es solo una advertencia: puede guardar igualmente.
            </div>
        </div>
    );
};
